import { useEffect } from 'react'

import * as Sentry from '@sentry/react'

import mixpanel from 'mixpanel-browser'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

const ENV = {
  local: {
    environment: 'local',
    enabled: false,
    tracePropagationTargets: ['localhost', 'https://api-dev.bitdex.xyz'],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  },
  development: {
    environment: 'development',
    enabled: false,
    tracePropagationTargets: ['https://api-dev.bitdex.xyz'],
    tracesSampleRate: 1,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
  },
  staging: {
    environment: 'staging',
    enabled: false,
    tracePropagationTargets: ['https://api-stg.bitdex.xyz'],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  },
  production: {
    environment: 'production',
    enabled: true,
    tracePropagationTargets: ['https://api.optimex.xyz'],
    tracesSampleRate: 1,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1.0,
  },
  'wallet-development': {
    environment: 'wallet-development',
    enabled: false,
    tracePropagationTargets: ['https://api-dev.bitdex.xyz'],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  },
  'wallet-staging': {
    environment: 'wallet-staging',
    enabled: false,
    tracePropagationTargets: ['https://api-stg.bitdex.xyz'],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  },
  prelive: {
    environment: 'prelive',
    enabled: true,
    tracePropagationTargets: ['https://pre-api.optimex.xyz'],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  },
}

const getCurrentEnv = () => {
  if (window.location.hostname === 'localhost') return 'local'

  if (window.location.hostname.includes('wallet')) {
    if (window.location.hostname.includes('dev')) return 'wallet-development'
    if (window.location.hostname.includes('stg')) return 'wallet-staging'
  }

  if (window.location.hostname.includes('dev')) return 'development'
  if (window.location.hostname.includes('stg')) return 'staging'
  if (window.location.hostname.includes('pre')) return 'prelive'

  return 'production'
}

const env = getCurrentEnv()
const config = ENV[env]

if (config?.enabled) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: config.environment,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['log', 'warn', 'error'],
      }),
    ],

    tracesSampleRate: config.tracesSampleRate,

    tracePropagationTargets: config.tracePropagationTargets,

    debug: false,

    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    beforeSend(event, hint) {
      if (event.exception?.values?.some((e) => e.type === 'OptimexCustomError')) {
        return null // Ignore all WalletError instances
      }
      const error = hint?.originalException as Error
      const { name, message } = error
      const matchError = (msg: string) =>
        name?.toLowerCase()?.includes(msg.toLowerCase()) || message?.toLowerCase()?.includes(msg.toLowerCase())

      const ignoreErrors = [
        'User rejected',
        'User denied transaction signature',
        'Connection rejected',
        'User closed the wallet popup',
        'QuickNode Error: Transaction',
        'Popup closed',
      ]
      if (ignoreErrors.some(matchError)) {
        return null
      }

      return event
    },
  })

  const MIXPANEL_TOKEN = 'aa2f8aa095b95bff5ca19295a25b2b2a' // safe for FE
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: false,
  })
}

// polyfill
if (!('structuredClone' in globalThis)) {
  globalThis.structuredClone = (v) => v
}
